//React
import React, { useState } from "react";

//mui
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Stack from "@mui/material/Stack";

//Project
import { useMenu } from "providers/Menu";
import type { MenuContextValue } from "types/menu";
import AccountMenu from "containers/Menu/AccountMenu";
import SigilRemovalTracker from "UIComponents/SigilRemovalTracker";
import { UpgradeButton } from "UIComponents/UpgradeButton";
import FreeCreditModal from "UIComponents/FreeCreditModal/FreeCreditModal";
import FreeCreditPill from "UIComponents/FreeCreditPill/FreeCreditPill";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useGlobalConfig } from "providers/GlobalConfig";
import { InternalSessionChip } from "./InternalSessionChip";

const drawerWidth = 240;

interface PageProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
}

interface StyledAppBarProps extends AppBarProps {
  open?: boolean;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<StyledAppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Page = ({ children, title }: PageProps) => {
  // The provider exposes `isMiniMode` at the top level; the shared type still models it under `state`.
  const menuContext = useMenu() as (MenuContextValue & { isMiniMode?: boolean }) | null;
  const { toggleThis, isMiniMode } = menuContext ?? {};
  const [freeCreditsModalOpen, setFreeCreditsModalOpen] = useState(false);
  const analytics = getAnalytics();
  const { globalConfig } = useGlobalConfig() || {};
  const companyName = globalConfig?.tenant?.name;

  const toFullMode = () => {
    toggleThis?.("isMiniMode", false);
  };

  const handleOpenFreeCreditsModal = () => {
    void logEvent(analytics, "button_click", {
      event_label: "free_credits_modal_opened",
    });
    setFreeCreditsModalOpen(true);
  };

  const handleCloseFreeCreditsModal = () => {
    setFreeCreditsModalOpen(false);
  };

  return (
    <React.Fragment>
      <Box sx={{ display: "flex", width: 1 }}>
        <CssBaseline />
        <AppBar position="fixed" open={!isMiniMode}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Toolbar sx={{ display: "flex" }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={toFullMode}
                edge="start"
                sx={{
                  marginRight: "36px",
                  ...(!isMiniMode && { display: "none" }),
                }}
              >
                <MenuIcon />
              </IconButton>
              <Stack spacing={0}>
                <Typography
                  sx={{ fontFamily: "Oswald", lineHeight: 1.1 }}
                  variant="h5"
                  noWrap
                >
                  {title}
                </Typography>

                {companyName && (
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.75,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                    noWrap
                  >
                    {companyName}
                  </Typography>
                )}
              </Stack>
            </Toolbar>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={1}
              sx={{ marginRight: "12px" }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{ mr: 1.5 }}
              >
                <InternalSessionChip />
                <SigilRemovalTracker />
                <FreeCreditPill onClick={handleOpenFreeCreditsModal} />

                <AccountMenu />
                <UpgradeButton showIcon={false} />
              </Stack>
            </Stack>
          </Stack>
        </AppBar>

        <Box sx={{ ml: 3, mr: 3, flexGrow: 1 }}>
          <DrawerHeader />
          {children}
        </Box>
      </Box>

      <FreeCreditModal
        open={freeCreditsModalOpen}
        onClose={handleCloseFreeCreditsModal}
      />
    </React.Fragment>
  );
};

export default Page;
